import { createAsyncThunk } from "@reduxjs/toolkit";
import dataService from "@/endpoints/dataService";

export const updateQuestionOptionColor = createAsyncThunk<
  | { entityId: number; color: string; entityType: "RESPONSE" | "USER_ROLLUP" }
  | null,
  {
    questionGroup: string;
    entityId: number;
    color: string;
    entityType: "RESPONSE" | "USER_ROLLUP";
  },
  { rejectValue: string }
>(
  "questions/updateQuestionOptionColor",
  async ({ questionGroup, entityId, color, entityType }, thunkAPI) => {
    try {
      const response: any =
        await dataService.questions.updateQuestionOptionColor(
          questionGroup,
          entityId,
          color,
          entityType,
        );
      if (response) {
        return { entityId, color, entityType };
      }
      return null;
    } catch (err: any) {
      return thunkAPI.rejectWithValue(
        "Failed to update question option color" + err.message,
      );
    }
  },
);

type SchemeBand = {
  label: string;
  key: string;
  ordinal: number;
  optionIds: (string | number)[];
  color?: string;
};

/**
 * One scheme per question holding every user rollup as a band.
 *  - POST (create): new bands, `ordinal` 1-based. Also used to add bands to
 *    a question that already has a scheme — the server appends them to it.
 *  - PUT (update): only existing bands being changed, matched by `key`,
 *    `ordinal` 0-based; it never adds bands. The response returns the full
 *    band list.
 *
 * The data service resolves HTTP failures to `{ error, errorCode }`; these
 * thunks turn that into a rejection so callers can tell failure apart.
 */
type SchemePayload = {
  bands: SchemeBand[];
};

export const createScheme = createAsyncThunk<
  any,
  { questionCode: string; payload: SchemePayload },
  { rejectValue: string }
>(
  "questions/createScheme",
  async ({ questionCode, payload }, thunkAPI) => {
    try {
      const response: any = await dataService.questions.createScheme(
        questionCode,
        payload,
      );
      if (response?.error) {
        return thunkAPI.rejectWithValue(
          "Failed to create scheme: " + response.error,
        );
      }
      return response;
    } catch (err: any) {
      return thunkAPI.rejectWithValue(
        "Failed to create scheme: " + err.message,
      );
    }
  },
);

export const updateScheme = createAsyncThunk<
  any,
  { questionCode: string; payload: SchemePayload },
  { rejectValue: string }
>(
  "questions/updateScheme",
  async ({ questionCode, payload }, thunkAPI) => {
    try {
      const response: any = await dataService.questions.updateScheme(
        questionCode,
        payload,
      );
      if (response?.error) {
        return thunkAPI.rejectWithValue(
          "Failed to update scheme: " + response.error,
        );
      }
      return response;
    } catch (err: any) {
      return thunkAPI.rejectWithValue(
        "Failed to update scheme: " + err.message,
      );
    }
  },
);

export const deleteScheme = createAsyncThunk<
  any,
  { questionCode: string; bandKey: string },
  { rejectValue: string }
>(
  "questions/deleteScheme",
  async ({ questionCode, bandKey }, thunkAPI) => {
    try {
      const response: any = await dataService.questions.deleteScheme(
        questionCode,
        bandKey,
      );
      if (response?.error) {
        return thunkAPI.rejectWithValue(
          "Failed to delete scheme: " + response.error,
        );
      }
      return response;
    } catch (err: any) {
      return thunkAPI.rejectWithValue(
        "Failed to delete scheme: " + err.message,
      );
    }
  },
);
